import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import {
    fetchAdminUsers,
    sendAdminUserPushNotification,
} from '../../../services/Http/AdminApi';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import MainButton from '../../../shared/layout/buttons/MainButton';
import AdminFilterBar from './components/AdminFilterBar';

const NOTIFICATION_TEMPLATES = [
    {
        key: 'purchase_confirmed',
        label: 'Purchase confirmed',
        type: 'purchase_confirmed',
        title: 'Purchase confirmed',
        body: 'Your purchase has been recorded and the warranty is active.',
        payload: { screen: 'item_detail' },
        action: { type: 'open_screen', screen: 'item_detail', label: 'View item' },
    },
    {
        key: 'warranty_expiring',
        label: 'Warranty expiring',
        type: 'warranty_expiring',
        title: 'Warranty expiring soon',
        body: 'Your warranty is expiring soon. Review coverage details.',
        payload: { screen: 'warranty_detail' },
        action: { type: 'open_screen', screen: 'warranty_detail', label: 'View warranty' },
    },
    {
        key: 'service_reminder',
        label: 'Service reminder',
        type: 'service_reminder',
        title: 'Service reminder',
        body: 'Your device is due for a service check.',
        payload: { screen: 'notifications' },
        action: { type: 'open_screen', screen: 'notifications', label: 'Open app' },
    },
    {
        key: 'loyalty_points_earned',
        label: 'Loyalty points earned',
        type: 'loyalty_points_earned',
        title: 'You earned loyalty points',
        body: 'New loyalty points were added to your account.',
        payload: { screen: 'campaign_detail' },
        action: { type: 'open_screen', screen: 'campaign_detail', label: 'View rewards' },
    },
    {
        key: 'new_discount',
        label: 'New discount',
        type: 'new_discount',
        title: 'New discount available',
        body: 'A new discount is available for your favorite products.',
        payload: { screen: 'discount_detail' },
        action: { type: 'open_screen', screen: 'discount_detail', label: 'View discount' },
    },
    {
        key: 'security_alert',
        label: 'Security alert',
        type: 'security_alert',
        title: 'Security alert',
        body: 'We detected an important account event. Please review your account activity.',
        payload: { screen: 'notifications' },
        action: { type: 'open_screen', screen: 'notifications', label: 'Review' },
    },
    {
        key: 'custom',
        label: 'Custom (random/editable)',
        type: 'custom',
        title: 'Custom notification',
        body: 'Customize title, body, payload and action as needed.',
        payload: { screen: 'notifications' },
        action: { type: 'open_screen', screen: 'notifications', label: 'Open' },
    },
];

const ACTION_TYPE_OPTIONS = [
    { value: 'none', label: 'No action' },
    { value: 'open_screen', label: 'Open screen' },
    { value: 'open_url', label: 'Open URL' },
    { value: 'custom', label: 'Custom action JSON' },
];

const randomFrom = (items) => items[Math.floor(Math.random() * items.length)];

const randomCustomSeed = () => {
    const titles = [
        'Quick update for your account',
        'Important notice',
        'Action recommended',
        'We have news for you',
    ];
    const bodies = [
        'Tap to view the latest details in your app.',
        'Please review the new update and continue.',
        'A new event was added to your account.',
        'Open the app to check the new information.',
    ];
    const screens = ['notifications', 'item_detail', 'campaign_detail', 'discount_detail'];

    const screen = randomFrom(screens);
    return {
        type: 'custom',
        title: randomFrom(titles),
        body: randomFrom(bodies),
        payload: {
            screen,
            ref: `custom_${Math.random().toString(36).slice(2, 10)}`,
        },
        action: {
            type: 'open_screen',
            screen,
            label: 'Open',
        },
    };
};

const serializePretty = (value) => JSON.stringify(value || {}, null, 2);

const AdminNotifications = () => {
    const [search, setSearch] = useState('');
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [users, setUsers] = useState([]);
    const [selectedUserIds, setSelectedUserIds] = useState([]);

    const [templateKey, setTemplateKey] = useState('purchase_confirmed');
    const [type, setType] = useState('purchase_confirmed');
    const [title, setTitle] = useState('Purchase confirmed');
    const [body, setBody] = useState('Your purchase has been recorded and the warranty is active.');
    const [payloadText, setPayloadText] = useState(serializePretty({ screen: 'item_detail' }));
    const [priority, setPriority] = useState('normal');
    const [sendPush, setSendPush] = useState(true);
    const [channelPushEnabled, setChannelPushEnabled] = useState(true);

    const [actionType, setActionType] = useState('open_screen');
    const [actionLabel, setActionLabel] = useState('View item');
    const [actionScreen, setActionScreen] = useState('item_detail');
    const [actionUrl, setActionUrl] = useState('');
    const [actionCustomText, setActionCustomText] = useState(serializePretty({ type: 'open_screen', screen: 'item_detail' }));

    const [sending, setSending] = useState(false);

    const selectedCount = selectedUserIds.length;

    const visibleSelected = useMemo(
        () => users.filter((u) => selectedUserIds.includes(u.id)).length,
        [users, selectedUserIds]
    );

    const allVisibleSelected = users.length > 0 && users.every((u) => selectedUserIds.includes(u.id));

    const loadUsers = useCallback(async (searchText) => {
        setLoadingUsers(true);
        try {
            const response = await fetchAdminUsers({
                search: searchText,
                per_page: 50,
                sort_by: 'created_at',
                sort_dir: 'desc',
            });
            setUsers(response?.data || []);
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to load users');
        } finally {
            setLoadingUsers(false);
        }
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            loadUsers(search.trim());
        }, 250);

        return () => clearTimeout(timer);
    }, [search, loadUsers]);

    const applyTemplate = useCallback((key) => {
        if (key === 'custom') {
            const customSeed = randomCustomSeed();
            setType(customSeed.type);
            setTitle(customSeed.title);
            setBody(customSeed.body);
            setPayloadText(serializePretty(customSeed.payload));
            setActionType('open_screen');
            setActionLabel(customSeed.action.label || 'Open');
            setActionScreen(customSeed.action.screen || 'notifications');
            setActionUrl('');
            setActionCustomText(serializePretty(customSeed.action));
            return;
        }

        const template = NOTIFICATION_TEMPLATES.find((item) => item.key === key);
        if (!template) return;

        setType(template.type);
        setTitle(template.title);
        setBody(template.body);
        setPayloadText(serializePretty(template.payload));
        setActionType(template.action?.type || 'none');
        setActionLabel(template.action?.label || 'Open');
        setActionScreen(template.action?.screen || 'notifications');
        setActionUrl(template.action?.url || '');
        setActionCustomText(serializePretty(template.action || {}));
    }, []);

    useEffect(() => {
        applyTemplate(templateKey);
    }, [templateKey, applyTemplate]);

    const toggleUser = (userId) => {
        setSelectedUserIds((prev) => {
            if (prev.includes(userId)) {
                return prev.filter((id) => id !== userId);
            }
            return [...prev, userId];
        });
    };

    const toggleSelectVisible = () => {
        setSelectedUserIds((prev) => {
            const visibleIds = users.map((user) => user.id);
            if (allVisibleSelected) {
                return prev.filter((id) => !visibleIds.includes(id));
            }
            const next = new Set(prev);
            visibleIds.forEach((id) => next.add(id));
            return Array.from(next);
        });
    };

    const buildAction = () => {
        if (actionType === 'none') {
            return null;
        }

        if (actionType === 'open_screen') {
            return {
                type: 'open_screen',
                label: actionLabel || 'Open',
                screen: actionScreen || 'notifications',
            };
        }

        if (actionType === 'open_url') {
            return {
                type: 'open_url',
                label: actionLabel || 'Open',
                url: actionUrl,
            };
        }

        if (actionType === 'custom') {
            const parsed = JSON.parse(actionCustomText || '{}');
            if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
                throw new Error('Custom action JSON must be an object');
            }
            return parsed;
        }

        return null;
    };

    const validateDeepLinkRequirements = (payload, action) => {
        const screen = (action?.screen || payload?.screen || '').toString();
        if (!screen) return null;

        if (screen === 'item_detail') {
            if (!payload?.item_id && !payload?.itemId) {
                return 'Payload must include item_id for item_detail notifications.';
            }
        }

        if (screen === 'discount_detail') {
            if (!payload?.discount_id && !payload?.discountId) {
                return 'Payload must include discount_id for discount_detail notifications.';
            }
        }

        if (screen === 'organization_detail') {
            if (!payload?.organization_id && !payload?.organizationId) {
                return 'Payload must include organization_id for organization_detail notifications.';
            }
        }

        if ((action?.type || '').toString().toLowerCase() === 'open_url') {
            const rawUrl = (action?.url || payload?.url || '').toString().trim();
            if (!rawUrl) {
                return 'Action URL is required for open_url actions.';
            }
            try {
                // Validate URL format early to avoid broken mobile actions.
                new URL(rawUrl);
            } catch (_) {
                return 'Action URL is not a valid absolute URL.';
            }
        }

        return null;
    };

    const onSend = async () => {
        if (selectedUserIds.length === 0) {
            toast.error('Select at least one user');
            return;
        }

        if (!type.trim()) {
            toast.error('Type is required');
            return;
        }

        if (!title.trim()) {
            toast.error('Title is required');
            return;
        }

        let payload;
        try {
            payload = JSON.parse(payloadText || '{}');
            if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) {
                throw new Error('Payload JSON must be an object');
            }
        } catch (error) {
            toast.error(`Invalid payload JSON: ${error.message}`);
            return;
        }

        let action;
        try {
            action = buildAction();
        } catch (error) {
            toast.error(error.message || 'Invalid action data');
            return;
        }

        const requirementError = validateDeepLinkRequirements(payload, action);
        if (requirementError) {
            toast.error(requirementError);
            return;
        }

        setSending(true);
        try {
            const response = await sendAdminUserPushNotification({
                user_ids: selectedUserIds,
                type,
                title,
                body,
                priority,
                payload,
                action,
                channel_push_enabled: channelPushEnabled,
                send_push: sendPush,
            });

            toast.success(`Notification sent (${response?.count || selectedUserIds.length})`);
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to send notification');
        } finally {
            setSending(false);
        }
    };

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle>User Notifications</PageTitle>
            </PageHeader>
            <PageContent>
                <AdminFilterBar>
                    <FilterGroup>
                        <SearchInput
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search users by name, email or ID"
                        />
                        <MainButton size="small" variant="secondary" onClick={() => loadUsers(search.trim())}>
                            Refresh users
                        </MainButton>
                    </FilterGroup>
                    <StatsText>
                        Selected: {selectedCount} | Visible selected: {visibleSelected}
                    </StatsText>
                </AdminFilterBar>

                <Panel>
                    <PanelHeader>
                        <h4>Recipients</h4>
                        <MainButton size="small" variant="secondary" onClick={toggleSelectVisible}>
                            {allVisibleSelected ? 'Unselect visible' : 'Select visible'}
                        </MainButton>
                    </PanelHeader>
                    <RecipientsList>
                        {loadingUsers && <MutedText>Loading users...</MutedText>}
                        {!loadingUsers && users.length === 0 && <MutedText>No users found</MutedText>}
                        {!loadingUsers && users.map((user) => {
                            const checked = selectedUserIds.includes(user.id);
                            return (
                                <RecipientRow key={user.id}>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => toggleUser(user.id)}
                                        />
                                        <RecipientText>
                                            <strong>{[user.name, user.surname].filter(Boolean).join(' ') || 'Unnamed user'}</strong>
                                            <span>{user.email || user.id}</span>
                                        </RecipientText>
                                    </label>
                                </RecipientRow>
                            );
                        })}
                    </RecipientsList>
                </Panel>

                <Panel>
                    <PanelHeader>
                        <h4>Notification</h4>
                        {templateKey === 'custom' && (
                            <MainButton
                                size="small"
                                variant="secondary"
                                onClick={() => applyTemplate('custom')}
                            >
                                Randomize custom
                            </MainButton>
                        )}
                    </PanelHeader>
                    <FormGrid>
                        <Field>
                            <label>Template</label>
                            <select value={templateKey} onChange={(e) => setTemplateKey(e.target.value)}>
                                {NOTIFICATION_TEMPLATES.map((template) => (
                                    <option key={template.key} value={template.key}>{template.label}</option>
                                ))}
                            </select>
                        </Field>
                        <Field>
                            <label>Type</label>
                            <input value={type} onChange={(e) => setType(e.target.value)} />
                        </Field>
                        <Field>
                            <label>Priority</label>
                            <select value={priority} onChange={(e) => setPriority(e.target.value)}>
                                <option value="normal">normal</option>
                                <option value="high">high</option>
                            </select>
                        </Field>
                        <CheckField>
                            <label>
                                <input type="checkbox" checked={sendPush} onChange={(e) => setSendPush(e.target.checked)} />
                                Send push
                            </label>
                        </CheckField>
                        <CheckField>
                            <label>
                                <input type="checkbox" checked={channelPushEnabled} onChange={(e) => setChannelPushEnabled(e.target.checked)} />
                                Channel push enabled
                            </label>
                        </CheckField>
                        <Field $span={2}>
                            <label>Title</label>
                            <input value={title} onChange={(e) => setTitle(e.target.value)} />
                        </Field>
                        <Field $span={2}>
                            <label>Body</label>
                            <textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} />
                        </Field>
                        <Field $span={2}>
                            <label>Payload JSON</label>
                            <textarea rows={8} value={payloadText} onChange={(e) => setPayloadText(e.target.value)} />
                        </Field>
                    </FormGrid>
                </Panel>

                <Panel>
                    <PanelHeader>
                        <h4>Action</h4>
                    </PanelHeader>
                    <FormGrid>
                        <Field>
                            <label>Action type</label>
                            <select value={actionType} onChange={(e) => setActionType(e.target.value)}>
                                {ACTION_TYPE_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </Field>
                        <Field>
                            <label>Button label</label>
                            <input value={actionLabel} onChange={(e) => setActionLabel(e.target.value)} />
                        </Field>

                        {actionType === 'open_screen' && (
                            <Field>
                                <label>Screen</label>
                                <input value={actionScreen} onChange={(e) => setActionScreen(e.target.value)} placeholder="item_detail" />
                            </Field>
                        )}

                        {actionType === 'open_url' && (
                            <Field $span={2}>
                                <label>URL</label>
                                <input value={actionUrl} onChange={(e) => setActionUrl(e.target.value)} placeholder="https://example.com" />
                            </Field>
                        )}

                        {actionType === 'custom' && (
                            <Field $span={2}>
                                <label>Custom action JSON</label>
                                <textarea rows={6} value={actionCustomText} onChange={(e) => setActionCustomText(e.target.value)} />
                            </Field>
                        )}
                    </FormGrid>
                </Panel>

                <ActionsRow>
                    <MainButton onClick={onSend} disabled={sending}>
                        {sending ? 'Sending...' : 'Send notification'}
                    </MainButton>
                </ActionsRow>
            </PageContent>
        </PageContainer>
    );
};

export default AdminNotifications;

const Panel = styled.section`
    border: 1px solid ${({ theme }) => theme.colors.border || '#e5e7eb'};
    border-radius: 12px;
    background: ${({ theme }) => theme.colors.surface || '#fff'};
    padding: 1rem;
    margin-bottom: 1rem;
`;

const PanelHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.75rem;

    h4 {
        margin: 0;
        font-size: 1rem;
        color: ${({ theme }) => theme.colors.textPrimary || '#111827'};
    }
`;

const FilterGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
    flex: 1;
`;

const SearchInput = styled.input`
    width: 100%;
    max-width: 400px;
    border: 1px solid ${({ theme }) => theme.colors.border || '#e5e7eb'};
    border-radius: 10px;
    padding: 0.5625rem 0.75rem;
    background: ${({ theme }) => theme.colors.surface || '#fff'};
    color: ${({ theme }) => theme.colors.textPrimary || '#111827'};
`;

const StatsText = styled.div`
    color: ${({ theme }) => theme.colors.textMuted || '#6b7280'};
    font-size: 0.8125rem;
`;

const RecipientsList = styled.div`
    max-height: 280px;
    overflow: auto;
    border: 1px solid ${({ theme }) => theme.colors.border || '#e5e7eb'};
    border-radius: 10px;
`;

const RecipientRow = styled.div`
    padding: 0.625rem 0.75rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border || '#e5e7eb'};

    &:last-child {
        border-bottom: none;
    }

    label {
        display: flex;
        gap: 0.625rem;
        align-items: flex-start;
        cursor: pointer;
    }
`;

const RecipientText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.125rem;

    strong {
        color: ${({ theme }) => theme.colors.textPrimary || '#111827'};
        font-size: 0.875rem;
    }

    span {
        color: ${({ theme }) => theme.colors.textMuted || '#6b7280'};
        font-size: 0.75rem;
    }
`;

const MutedText = styled.div`
    color: ${({ theme }) => theme.colors.textMuted || '#6b7280'};
    font-size: 0.8125rem;
    padding: 0.625rem 0.75rem;
`;

const FormGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    grid-column: span ${({ $span }) => ($span ? Number($span) : 1)};

    @media (max-width: 900px) {
        grid-column: span 1;
    }

    label {
        color: ${({ theme }) => theme.colors.textSecondary || '#374151'};
        font-size: 0.8125rem;
        font-weight: 600;
    }

    input,
    textarea,
    select {
        border: 1px solid ${({ theme }) => theme.colors.border || '#e5e7eb'};
        border-radius: 10px;
        padding: 0.5625rem 0.75rem;
        background: ${({ theme }) => theme.colors.surface || '#fff'};
        color: ${({ theme }) => theme.colors.textPrimary || '#111827'};
        font-size: 0.875rem;
    }

    textarea {
        resize: vertical;
        min-height: 88px;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
    }
`;

const CheckField = styled.div`
    display: flex;
    align-items: flex-end;

    label {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        color: ${({ theme }) => theme.colors.textSecondary || '#374151'};
        font-size: 0.8125rem;
        font-weight: 600;
    }
`;

const ActionsRow = styled.div`
    display: flex;
    justify-content: flex-end;
`;
